import { AuthRoleIdEnum } from 'constants/user'
import { isAdminOrOwner } from 'utils/auth/role'

/**
 * What a viewer may do to one Stack.
 *
 * Every Stack surface — the detail pane header, the mobile action sheet, the
 * Drill inspector, the list rows — asks this one function, so a capability can
 * never be granted on one surface and withheld on another.
 *
 * The model, in one line: reading a published Stack is an Organization-wide
 * right, authoring it belongs to the people attached to it.
 *
 *  - Owner / Admin — everything on any Stack of the org.
 *  - Coach — authoring only on a Stack they own or collaborate on. On everyone
 *    else's Stack they are a reader, plus commenting once it is published. Adding
 *    collaborators is the creator's own capability: a collaborator may work on the
 *    Stack but not hand access to someone else.
 *  - Player — reading only, and no comments at all.
 *
 * Archived is a read-only state for everybody: the Stack is out of circulation,
 * so only restoring it is left.
 */
export interface IStackCapabilityViewer {
  /** Max org role across the viewer's teams (`authStore.maxRoleAcrossTeams`). */
  roleId?: AuthRoleIdEnum | null
  /**
   * OrgMembership.id of the viewer in the active org — the only identity these
   * rules read. A User.id spans organizations, so it cannot say whether the
   * viewer authors this Stack here.
   */
  membershipId?: number | null
}

/**
 * The Stack fields the decision reads. Shaped to accept an `IProcess` as-is.
 *
 * Collaboration rows reach the client from two places and both carry the
 * membership id: the list pipelines look them up as `collaborators`, and the
 * detail endpoint includes them as `collaborations` beside the resolved Users
 * (`process.collaborators`, which are Users and carry no membership id).
 */
export interface IStackCapabilitySubject {
  ownerMembershipId?: number | null
  collaborators?: ({ membershipId?: number | null } | null)[] | null
  collaborations?: ({ membershipId?: number | null } | null)[] | null
  isPublished?: boolean
  archivedAt?: string | number | Date | null
}

export interface IStackCapabilities {
  /** See a Stack that is not published. A published one is readable by the whole org. */
  canViewDraft: boolean
  /** Edit the Stack header, its Drills and their content. */
  canEdit: boolean
  canPublish: boolean
  /** Archive, or restore what is archived. */
  canArchive: boolean
  canManageCollaborator: boolean
  /** Take an edit lock away from whoever holds it. */
  canForceUnlock: boolean
  /** Write a comment and read the thread — the two move together in this model. */
  canComment: boolean
}

const NO_CAPABILITIES: IStackCapabilities = {
  canViewDraft: false,
  canEdit: false,
  canPublish: false,
  canArchive: false,
  canManageCollaborator: false,
  canForceUnlock: false,
  canComment: false
}

/**
 * Whether the viewer is attached to this Stack as its owner or a collaborator.
 *
 * Both sides match on OrgMembership.id, mirroring the API. A Stack or a
 * Collaboration row left without one by the migration has no author to
 * recognise, and only an Owner/Admin reaches it.
 */
function membershipIdOf(viewer: IStackCapabilityViewer): number {
  const membershipId = Number(viewer.membershipId)
  return Number.isFinite(membershipId) && membershipId > 0 ? membershipId : 0
}

/** Created the Stack: the membership recorded as its owner. */
function ownsStack(viewer: IStackCapabilityViewer, stack: IStackCapabilitySubject): boolean {
  const membershipId = membershipIdOf(viewer)
  if (membershipId === 0 || stack.ownerMembershipId == null) return false
  return Number(stack.ownerMembershipId) === membershipId
}

/** Added to the Stack by its creator or an Owner/Admin. */
function collaboratesOnStack(viewer: IStackCapabilityViewer, stack: IStackCapabilitySubject): boolean {
  const membershipId = membershipIdOf(viewer)
  if (membershipId === 0) return false
  const collaborationRows = [...(stack.collaborations ?? []), ...(stack.collaborators ?? [])]
  return collaborationRows.some(row => row?.membershipId != null && Number(row.membershipId) === membershipId)
}

export function getStackCapabilities(
  viewer: IStackCapabilityViewer,
  stack?: IStackCapabilitySubject | null
): IStackCapabilities {
  const roleId = viewer.roleId ?? AuthRoleIdEnum.PLAYER
  if (roleId === AuthRoleIdEnum.PLAYER || !stack) return NO_CAPABILITIES

  const isAdmin = isAdminOrOwner(roleId)
  const isArchived = !!stack.archivedAt
  const isPublished = !!stack.isPublished
  // An Admin answers for every Stack in the org; a Coach only for their own. The
  // creator is kept apart from a collaborator, because one capability separates
  // them: who else may work on the Stack is the creator's call.
  const isCreator = isAdmin || ownsStack(viewer, stack)
  const isAuthor = isCreator || collaboratesOnStack(viewer, stack)

  return {
    canViewDraft: isAuthor,
    canEdit: isAuthor && !isArchived,
    canPublish: isAuthor && !isArchived && !isPublished,
    canArchive: isAuthor,
    canManageCollaborator: isCreator && !isArchived,
    canForceUnlock: isAdmin,
    // A Coach comments on any published Stack, not only their own — reviewing a
    // colleague's work is the point. A draft stays private to its authors.
    canComment: !isArchived && (isPublished || isAuthor)
  }
}
